import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  Box,
  Film,
  FlaskConical,
  Grid3x3,
  Layers,
  Loader2,
  Package,
  Printer,
  Shield,
  ShoppingBag,
  Sparkles,
  Sticker,
  Tags,
  Boxes,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { OptionCard } from "./options/OptionCard";

type OptionItem = {
  title: string;
  description: string;
  image: string;
};

type OptionGroup = {
  label: string;
  items: OptionItem[];
};

type OptionsCategory = {
  id: string;
  label: string;
  items?: OptionItem[];
  groups?: OptionGroup[];
};

type OptionsLibraryPayload = {
  categories: OptionsCategory[];
};

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  paperboard: Box,
  coatingType: Sparkles,
  laminationType: Film,
  finishes: Package,
  printing: Printer,
  corrugated: Layers,
  flutes: Grid3x3,
  plastic: FlaskConical,
  foam: Shield,
  boxTypes: Boxes,
  stickers: Sticker,
  labels: Tags,
  bagHandles: ShoppingBag,
};

export type ProductOptionsTabProps = {
  /** Category ids from `public/options-library.json`, in tab order. */
  categoryIds: string[];
};

export function ProductOptionsTab({ categoryIds }: ProductOptionsTabProps) {
  const [library, setLibrary] = useState<OptionsLibraryPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    fetch("/options-library.json")
      .then((res) => {
        if (!res.ok) throw new Error("Could not load options");
        return res.json() as Promise<OptionsLibraryPayload>;
      })
      .then((data) => {
        if (cancelled) return;
        setLibrary(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError("We couldn’t load the options catalog. Please try again later.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCategories = useMemo(() => {
    if (!library?.categories.length || !categoryIds.length) return [];
    const byId = new Map(library.categories.map((c) => [c.id, c]));
    return categoryIds.map((id) => byId.get(id)).filter((c): c is OptionsCategory => Boolean(c));
  }, [library, categoryIds]);

  useEffect(() => {
    if (!filteredCategories.length) {
      setActiveTab("");
      return;
    }
    const first = filteredCategories[0].id;
    setActiveTab((prev) => (filteredCategories.some((c) => c.id === prev) ? prev : first));
  }, [filteredCategories]);

  const activeCategory = useMemo(() => {
    if (!activeTab) return null;
    return filteredCategories.find((c) => c.id === activeTab) ?? null;
  }, [filteredCategories, activeTab]);

  if (categoryIds.length === 0) {
    return (
      <section className="py-20 bg-[#F6F2EB] border-t border-[#131312]/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <Link
            to="/options"
            className="inline-flex items-center justify-center px-6 py-3 bg-[#131312] text-white transition-colors hover:bg-[#131312]/90 rounded-xl font-medium"
          >
            View options library
            <ArrowRight className="ml-2 size-4" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-[#FBFAF7] border-t border-[#131312]/5">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-[#131312]">Customization Options</h2>
          <p className="text-xl text-[#131312]/60 max-w-2xl mx-auto">
            Explore the materials, finishes, and printing options available for this product
          </p>
        </div>

        {loadError && (
          <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-center text-destructive mb-8">
            {loadError}
          </p>
        )}

        {!loadError && !library && (
          <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" aria-hidden />
            <span>Loading options…</span>
          </div>
        )}

        {!loadError && library && filteredCategories.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            No matching categories in the library for this product.
          </p>
        )}

        {!loadError && filteredCategories.length > 0 && (
          <div className="bg-[#F6F2EB] rounded-3xl overflow-hidden border border-[#131312]/5">
            <div className="flex overflow-x-auto gap-2 border-b border-[#131312]/10 px-2 py-3 sm:px-4 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]">
              {filteredCategories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] ?? Box;
                const isActive = activeTab === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setActiveTab(cat.id)}
                    className={`shrink-0 inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors sm:px-4 sm:py-3 sm:text-base ${
                      isActive
                        ? "border-[#131312] bg-[#131312] text-white shadow-lg shadow-[#131312]/20"
                        : "border-[#131312]/10 bg-[#FBFAF7] text-[#131312] hover:bg-[#efe9e0]"
                    }`}
                  >
                    <Icon className="size-4 shrink-0 sm:size-5" />
                    <span className="font-medium whitespace-nowrap">{cat.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-4 sm:p-8 bg-[#F6F2EB]">
              {activeCategory?.groups?.length ? (
                <div className="space-y-12">
                  {activeCategory.groups.map((group, gi) => (
                    <section key={`${activeCategory.id}-g-${gi}`}>
                      <header className="mb-4 border-b border-border/60 pb-3">
                        <h3 className="text-lg font-bold">{group.label}</h3>
                      </header>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.items.slice(0, 3).map((option, index) => (
                          <OptionCard key={`${group.label}-${option.title}-${index}`} option={option} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(activeCategory?.items ?? []).slice(0, 3).map((option, index) => (
                    <OptionCard key={`${option.title}-${index}`} option={option} />
                  ))}
                </div>
              )}

              <div className="mt-8 text-center">
                <Link
                  to="/options"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#131312] text-white transition-colors hover:bg-[#131312]/90 rounded-xl font-medium"
                >
                  Open full options library
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
